// src/components/tools/business-model-canvas/BusinessModelCanvasTool.jsx
// AI-Powered Business Model Canvas Generator (Strategyzer Model)
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Edit2, Eye, Loader2,
  RefreshCw, Save, Sparkles, Target, Users, Truck, Heart, DollarSign,
  Key, Cog, Handshake, Wallet, Download, Share2, MoreVertical, Trash2,
  Plus, FileText, Copy, CheckCircle, AlertCircle, Lightbulb, HelpCircle,
  ChevronDown, ChevronUp, Maximize2, Minimize2, Grid, List, X
} from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ============================================================
// BUSINESS MODEL CANVAS SECTIONS (Strategyzer Order)
// ============================================================

const BMC_SECTIONS = [
  {
    id: 'valueProposition',
    name: 'Value Propositions',
    shortName: 'Value Props',
    icon: Target,
    color: 'indigo',
    gridArea: 'vp',
    description: 'What value do you deliver to the customer? Which customer problems are you helping to solve?',
    questions: [
      { id: 'q1', text: 'What products or services do you offer?', placeholder: 'e.g., Mobile app for fitness tracking, Consulting services...' },
      { id: 'q2', text: 'What customer problems or needs do you address?', placeholder: 'e.g., Lack of motivation to exercise, Need for personalized advice...' },
      { id: 'q3', text: 'What makes your offering unique or different?', placeholder: 'e.g., AI-powered recommendations, 24/7 support, Lower cost...' },
      { id: 'q4', text: 'What benefits do customers gain from your solution?', placeholder: 'e.g., Save time, Better health outcomes, Increased revenue...' }
    ],
    promptTemplate: `Generate a concise Value Proposition section for a Business Model Canvas based on these inputs:

Products/Services: {q1}
Customer Problems Addressed: {q2}
Unique Differentiators: {q3}
Customer Benefits: {q4}

Format the response as 4-6 bullet points that clearly articulate:
- The core value delivered
- Key benefits for customers
- What makes this unique
Keep each point concise (1-2 sentences max). Use business-appropriate language.`
  },
  {
    id: 'customerSegments',
    name: 'Customer Segments',
    shortName: 'Customers',
    icon: Users,
    color: 'emerald',
    gridArea: 'cs',
    description: 'For whom are you creating value? Who are your most important customers?',
    questions: [
      { id: 'q1', text: 'Who are your target customers? (demographics, industries)', placeholder: 'e.g., Small business owners aged 25-45, Healthcare providers...' },
      { id: 'q2', text: 'What are their key characteristics or behaviors?', placeholder: 'e.g., Tech-savvy, Budget-conscious, Time-poor...' },
      { id: 'q3', text: 'What specific needs or pain points do they have?', placeholder: 'e.g., Need to reduce costs, Want better engagement...' },
      { id: 'q4', text: 'Are there different customer segments with different needs?', placeholder: 'e.g., Enterprise vs SMB, Beginners vs Advanced users...' }
    ],
    promptTemplate: `Generate a Customer Segments section for a Business Model Canvas.

Context from Value Proposition:
{previousSections}

Customer Inputs:
Target Customers: {q1}
Key Characteristics: {q2}
Needs/Pain Points: {q3}
Different Segments: {q4}

Create 3-5 distinct customer segment descriptions. For each segment include:
- Segment name
- Key characteristics
- Primary needs
Format as bullet points. Be specific and actionable.`
  },
  {
    id: 'channels',
    name: 'Channels',
    shortName: 'Channels',
    icon: Truck,
    color: 'blue',
    gridArea: 'ch',
    description: 'Through which channels do your customer segments want to be reached? How do you reach them now?',
    questions: [
      { id: 'q1', text: 'How will customers discover your offering?', placeholder: 'e.g., Social media, Google ads, Word of mouth, Trade shows...' },
      { id: 'q2', text: 'How will customers purchase or access your product/service?', placeholder: 'e.g., Website, App stores, Direct sales, Retail partners...' },
      { id: 'q3', text: 'How will you deliver value to customers?', placeholder: 'e.g., Digital download, Physical delivery, In-person service...' },
      { id: 'q4', text: 'How will you provide after-sales support?', placeholder: 'e.g., Help desk, Chat support, Community forum, Account managers...' }
    ],
    promptTemplate: `Generate a Channels section for a Business Model Canvas.

Previous Context:
{previousSections}

Channel Inputs:
Discovery Channels: {q1}
Purchase/Access Channels: {q2}
Delivery Methods: {q3}
Support Channels: {q4}

Create a comprehensive channels strategy covering:
- Awareness phase channels
- Evaluation phase channels
- Purchase phase channels
- Delivery phase channels
- After-sales phase channels
Format as bullet points grouped by phase.`
  },
  {
    id: 'customerRelationships',
    name: 'Customer Relationships',
    shortName: 'Relationships',
    icon: Heart,
    color: 'pink',
    gridArea: 'cr',
    description: 'What type of relationship does each customer segment expect? How do you establish and maintain them?',
    questions: [
      { id: 'q1', text: 'What type of relationship do customers expect? (self-service, personal, automated)', placeholder: 'e.g., Dedicated account manager, Self-service portal, Community...' },
      { id: 'q2', text: 'How will you acquire new customers?', placeholder: 'e.g., Free trials, Referral programs, Content marketing...' },
      { id: 'q3', text: 'How will you retain existing customers?', placeholder: 'e.g., Loyalty programs, Regular check-ins, Exclusive benefits...' },
      { id: 'q4', text: 'How will you grow revenue from existing customers?', placeholder: 'e.g., Upselling, Cross-selling, Premium tiers...' }
    ],
    promptTemplate: `Generate a Customer Relationships section for a Business Model Canvas.

Previous Context:
{previousSections}

Relationship Inputs:
Expected Relationship Type: {q1}
Customer Acquisition: {q2}
Customer Retention: {q3}
Revenue Growth: {q4}

Define the customer relationship strategy including:
- Relationship type for each segment
- Acquisition tactics
- Retention mechanisms
- Growth/upsell strategies
Format as organized bullet points.`
  },
  {
    id: 'revenueStreams',
    name: 'Revenue Streams',
    shortName: 'Revenue',
    icon: DollarSign,
    color: 'green',
    gridArea: 'rs',
    description: 'For what value are your customers willing to pay? How are they currently paying? How would they prefer to pay?',
    questions: [
      { id: 'q1', text: 'What are customers willing to pay for?', placeholder: 'e.g., Software subscription, Professional services, Products...' },
      { id: 'q2', text: 'What pricing model will you use?', placeholder: 'e.g., Monthly subscription, One-time purchase, Freemium, Usage-based...' },
      { id: 'q3', text: 'What are your expected price points?', placeholder: 'e.g., R99/month basic, R299/month pro, R5000 enterprise setup...' },
      { id: 'q4', text: 'Are there multiple revenue streams?', placeholder: 'e.g., Subscriptions + advertising, Product sales + services...' }
    ],
    promptTemplate: `Generate a Revenue Streams section for a Business Model Canvas.

Previous Context:
{previousSections}

Revenue Inputs:
What Customers Pay For: {q1}
Pricing Model: {q2}
Price Points: {q3}
Multiple Streams: {q4}

Define the revenue model including:
- Primary revenue streams
- Pricing mechanisms (fixed, dynamic, auction, etc.)
- Price points and tiers
- Revenue potential per segment
Format as bullet points with specific amounts where provided.`
  },
  {
    id: 'keyResources',
    name: 'Key Resources',
    shortName: 'Resources',
    icon: Key,
    color: 'amber',
    gridArea: 'kr',
    description: 'What key resources do your value propositions require? What resources are needed for channels, relationships, and revenue?',
    questions: [
      { id: 'q1', text: 'What physical resources do you need?', placeholder: 'e.g., Office space, Equipment, Inventory, Vehicles...' },
      { id: 'q2', text: 'What intellectual resources do you need?', placeholder: 'e.g., Patents, Brand, Proprietary data, Software...' },
      { id: 'q3', text: 'What human resources do you need?', placeholder: 'e.g., Developers, Sales team, Customer support, Management...' },
      { id: 'q4', text: 'What financial resources do you need?', placeholder: 'e.g., Working capital, Credit lines, Investment...' }
    ],
    promptTemplate: `Generate a Key Resources section for a Business Model Canvas.

Previous Context:
{previousSections}

Resource Inputs:
Physical Resources: {q1}
Intellectual Resources: {q2}
Human Resources: {q3}
Financial Resources: {q4}

List the essential resources categorized by:
- Physical (tangible assets)
- Intellectual (intangible assets)
- Human (people and skills)
- Financial (money and credit)
Format as bullet points grouped by category.`
  },
  {
    id: 'keyActivities',
    name: 'Key Activities',
    shortName: 'Activities',
    icon: Cog,
    color: 'purple',
    gridArea: 'ka',
    description: 'What key activities do your value propositions require? What activities are most important?',
    questions: [
      { id: 'q1', text: 'What production activities are needed?', placeholder: 'e.g., Software development, Manufacturing, Content creation...' },
      { id: 'q2', text: 'What problem-solving activities are needed?', placeholder: 'e.g., Consulting, Custom solutions, R&D...' },
      { id: 'q3', text: 'What platform/network activities are needed?', placeholder: 'e.g., Platform maintenance, Network management, Community building...' },
      { id: 'q4', text: 'What operational activities are critical?', placeholder: 'e.g., Quality control, Customer onboarding, Order fulfillment...' }
    ],
    promptTemplate: `Generate a Key Activities section for a Business Model Canvas.

Previous Context:
{previousSections}

Activity Inputs:
Production Activities: {q1}
Problem-Solving Activities: {q2}
Platform/Network Activities: {q3}
Operational Activities: {q4}

Identify the most important activities categorized by:
- Production (creating and delivering)
- Problem-solving (new solutions)
- Platform/Network (maintaining platforms)
- Operations (day-to-day running)
Format as prioritized bullet points.`
  },
  {
    id: 'keyPartnerships',
    name: 'Key Partnerships',
    shortName: 'Partners',
    icon: Handshake,
    color: 'orange',
    gridArea: 'kp',
    description: 'Who are your key partners? Who are your key suppliers? What key activities do partners perform?',
    questions: [
      { id: 'q1', text: 'What strategic alliances would benefit your business?', placeholder: 'e.g., Technology partners, Distribution partners, Marketing partners...' },
      { id: 'q2', text: 'What suppliers are critical to your operations?', placeholder: 'e.g., Raw material suppliers, Software vendors, Service providers...' },
      { id: 'q3', text: 'What activities could partners perform better or cheaper?', placeholder: 'e.g., Logistics, Payment processing, Customer support...' },
      { id: 'q4', text: 'What resources do you need from partners?', placeholder: 'e.g., Technology, Expertise, Distribution network, Capital...' }
    ],
    promptTemplate: `Generate a Key Partnerships section for a Business Model Canvas.

Previous Context:
{previousSections}

Partnership Inputs:
Strategic Alliances: {q1}
Critical Suppliers: {q2}
Outsourced Activities: {q3}
Partner Resources: {q4}

Define the partnership strategy including:
- Strategic alliance partners
- Key suppliers
- Joint venture opportunities
- Buyer-supplier relationships
Format as bullet points with rationale for each partnership.`
  },
  {
    id: 'costStructure',
    name: 'Cost Structure',
    shortName: 'Costs',
    icon: Wallet,
    color: 'red',
    gridArea: 'cost',
    description: 'What are the most important costs inherent in your business model? Which key resources and activities are most expensive?',
    questions: [
      { id: 'q1', text: 'What are your fixed costs?', placeholder: 'e.g., Salaries, Rent, Software licenses, Insurance...' },
      { id: 'q2', text: 'What are your variable costs?', placeholder: 'e.g., Materials, Transaction fees, Marketing spend, Commissions...' },
      { id: 'q3', text: 'Is your business more cost-driven or value-driven?', placeholder: 'e.g., Focus on lowest cost, Focus on premium value, Balance of both...' },
      { id: 'q4', text: 'What economies of scale or scope can you achieve?', placeholder: 'e.g., Bulk purchasing, Shared resources across products, Automation...' }
    ],
    promptTemplate: `Generate a Cost Structure section for a Business Model Canvas.

Previous Context:
{previousSections}

Cost Inputs:
Fixed Costs: {q1}
Variable Costs: {q2}
Cost vs Value Focus: {q3}
Economies of Scale/Scope: {q4}

Define the cost structure including:
- Major fixed costs
- Key variable costs
- Cost characteristics (cost-driven vs value-driven)
- Economies of scale opportunities
Format as bullet points with estimated proportions where possible.`
  }
];

// ============================================================
// CONSTANTS
// ============================================================

const ViewMode = {
  WIZARD: 'WIZARD',
  CANVAS: 'CANVAS',
  LIST: 'LIST'
};

// ============================================================
// AI SERVICE
// ============================================================

const generateSectionContent = async (section, answers, previousSections, bookId) => {
  try {
    const functions = getFunctions();
    const generateBMCSection = httpsCallable(functions, 'generateBMCSection');
    
    // Build previousSections with names for context
    const namedPreviousSections = {};
    BMC_SECTIONS.forEach(s => {
      if (previousSections[s.id]?.content) {
        namedPreviousSections[s.name] = { content: previousSections[s.id].content };
      }
    });

    const result = await generateBMCSection({
      sectionId: section.id,
      sectionName: section.name,
      promptTemplate: section.promptTemplate,
      answers,
      previousSections: namedPreviousSections,
      bookId
    });

    if (!result.data?.success) {
      throw new Error(result.data?.error || 'Generation failed');
    }

    return result.data.content;
  } catch (error) {
    console.error('AI generation error:', error);
    throw new Error(error.message || 'Failed to generate content');
  }
};

// ============================================================
// HELPER COMPONENTS
// ============================================================

const ProgressIndicator = ({ sections, currentIndex, completedSections, onJumpTo }) => (
  <div className="flex items-center gap-1 overflow-x-auto pb-2">
    {sections.map((section, index) => {
      const Icon = section.icon;
      const isCompleted = completedSections[section.id]?.content;
      const isCurrent = index === currentIndex;
      const isAccessible = index <= currentIndex || isCompleted;
      
      return (
        <React.Fragment key={section.id}>
          {index > 0 && (
            <div className={`w-8 h-0.5 ${isCompleted || index <= currentIndex ? 'bg-indigo-400' : 'bg-gray-200'}`} />
          )}
          <button
            onClick={() => isAccessible && onJumpTo(index)}
            disabled={!isAccessible}
            className={`flex items-center justify-center w-10 h-10 rounded-full transition-all flex-shrink-0 ${
              isCurrent 
                ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 scale-110' 
                : isCompleted
                  ? 'bg-emerald-500 text-white'
                  : isAccessible
                    ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    : 'bg-gray-50 text-gray-300 cursor-not-allowed'
            }`}
            title={section.name}
          >
            {isCompleted && !isCurrent ? (
              <Check className="w-5 h-5" />
            ) : (
              <Icon className="w-5 h-5" />
            )}
          </button>
        </React.Fragment>
      );
    })}
  </div>
);

const QuestionInput = ({ question, value, onChange }) => (
  <div className="space-y-2">
    <label className="flex items-start gap-2 text-sm font-medium text-gray-700">
      <HelpCircle className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
      {question.text}
    </label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(question.id, e.target.value)}
      placeholder={question.placeholder}
      rows={3}
      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
    />
  </div>
);

const ContentEditor = ({ content, onChange, onRegenerate, generating }) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-indigo-500" />
        <span className="font-medium text-gray-900">AI Generated Content</span>
      </div>
      <button
        onClick={onRegenerate}
        disabled={generating}
        className="flex items-center gap-2 px-3 py-1.5 text-sm text-indigo-600 hover:bg-indigo-50 rounded-lg disabled:opacity-50"
      >
        <RefreshCw className={`w-4 h-4 ${generating ? 'animate-spin' : ''}`} />
        Regenerate
      </button>
    </div>
    <textarea
      value={content}
      onChange={(e) => onChange(e.target.value)}
      rows={10}
      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 font-mono text-sm bg-gray-50"
      placeholder="Generated content will appear here..."
    />
    <p className="text-xs text-gray-500">
      Edit the content above as needed. Your changes will be saved and used as context for subsequent sections.
    </p>
  </div>
);

const SectionCard = ({ section, data, onClick, isEditing }) => {
  const Icon = section.icon;
  const hasContent = data?.content;

  return (
    <button
      onClick={onClick}
      className={`p-4 rounded-xl border-2 text-left transition-all hover:shadow-md ${
        isEditing
          ? `border-${section.color}-500 bg-${section.color}-50 shadow-lg`
          : hasContent
            ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-300'
            : 'border-gray-200 hover:border-gray-300 bg-white'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-${section.color}-100`}>
          <Icon className={`w-5 h-5 text-${section.color}-600`} />
        </div>
        {hasContent && <CheckCircle className="w-5 h-5 text-emerald-500" />}
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{section.shortName}</h3>
      {hasContent ? (
        <p className="text-xs text-gray-500 line-clamp-2">{data.content.substring(0, 100)}...</p>
      ) : (
        <p className="text-xs text-gray-400">Not completed</p>
      )}
    </button>
  );
};

// ============================================================
// CANVAS VIEW
// ============================================================

const CanvasView = ({ sections, sectionData, onEditSection }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const renderSection = (section) => {
    const Icon = section.icon;
    const data = sectionData[section.id];
    const hasContent = data?.content;

    return (
      <div
        key={section.id}
        className={`bg-white rounded-xl border-2 border-${section.color}-200 overflow-hidden flex flex-col`}
        style={{ gridArea: section.gridArea }}
      >
        {/* Header */}
        <div className={`px-4 py-3 bg-gradient-to-r from-${section.color}-50 to-${section.color}-100 border-b border-${section.color}-200`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon className={`w-5 h-5 text-${section.color}-600`} />
              <h3 className="font-semibold text-gray-900 text-sm">{section.shortName}</h3>
            </div>
            <button
              onClick={() => onEditSection(section)}
              className={`p-1.5 rounded-lg hover:bg-${section.color}-200 transition-colors`}
            >
              <Edit2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {hasContent ? (
            <div className="text-sm text-gray-700 whitespace-pre-wrap">
              {data.content}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-4">
              <Icon className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">Not completed</p>
              <button
                onClick={() => onEditSection(section)}
                className={`mt-2 text-xs text-${section.color}-600 hover:underline`}
              >
                Add content
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full overflow-auto p-6 bg-gray-50">
      {/* Canvas Grid - Strategyzer Layout */}
      <div 
        className="grid gap-4 min-h-[600px]"
        style={{
          gridTemplateColumns: 'repeat(10, 1fr)',
          gridTemplateRows: 'repeat(6, 1fr)',
          gridTemplateAreas: `
            "kp kp ka ka vp vp cr cr cs cs"
            "kp kp ka ka vp vp cr cr cs cs"
            "kp kp kr kr vp vp ch ch cs cs"
            "kp kp kr kr vp vp ch ch cs cs"
            "cost cost cost cost cost rs rs rs rs rs"
            "cost cost cost cost cost rs rs rs rs rs"
          `
        }}
      >
        {BMC_SECTIONS.map(section => renderSection(section))}
      </div>
    </div>
  );
};

// ============================================================
// SAVED CANVASES LIST
// ============================================================

const CanvasesList = ({ canvases, onSelect, onCreate, onDelete }) => {
  if (canvases.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
          <Grid className="w-10 h-10 text-indigo-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Business Models Yet</h3>
        <p className="text-gray-500 text-center mb-6 max-w-sm">
          Create your first Business Model Canvas to map out your business strategy
        </p>
        <button
          onClick={onCreate}
          className="px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Create Business Model
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canvases.map(canvas => {
        const completedCount = BMC_SECTIONS.filter(s => canvas.sections?.[s.id]?.content).length;
        
        return (
          <div
            key={canvas.id}
            className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">{canvas.name || 'Untitled Canvas'}</h3>
                <p className="text-sm text-gray-500 mb-3">{canvas.description || 'No description'}</p>
                
                {/* Progress */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                      style={{ width: `${(completedCount / BMC_SECTIONS.length) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-500">{completedCount}/{BMC_SECTIONS.length}</span>
                </div>

                {/* Section indicators */}
                <div className="flex items-center gap-1 flex-wrap">
                  {BMC_SECTIONS.map(section => {
                    const Icon = section.icon;
                    const hasContent = canvas.sections?.[section.id]?.content;
                    return (
                      <div
                        key={section.id}
                        className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                          hasContent ? `bg-${section.color}-100` : 'bg-gray-100'
                        }`}
                        title={section.name}
                      >
                        <Icon className={`w-4 h-4 ${hasContent ? `text-${section.color}-600` : 'text-gray-400'}`} />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => onSelect(canvas)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Delete this business model?')) onDelete(canvas.id);
                  }}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
              <span>Created: {new Date(canvas.createdAt?.toDate?.() || canvas.createdAt).toLocaleDateString()}</span>
              <span>Last updated: {new Date(canvas.updatedAt?.toDate?.() || canvas.updatedAt).toLocaleDateString()}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const BusinessModelCanvasTool = ({ bookId, customStyling = {}, toolsService, onBack }) => {
  // State
  const [canvases, setCanvases] = useState([]);
  const [activeCanvas, setActiveCanvas] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionData, setSectionData] = useState({});
  const [currentAnswers, setCurrentAnswers] = useState({});
  const [generatedContent, setGeneratedContent] = useState('');
  const [viewMode, setViewMode] = useState(ViewMode.WIZARD);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCanvasName, setNewCanvasName] = useState('');
  const [newCanvasDescription, setNewCanvasDescription] = useState('');
  
  const currentSection = BMC_SECTIONS[currentSectionIndex];

  // Load data
  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [canvasesData, settings] = await Promise.all([
        toolsService.getToolData(bookId, 'business-model-canvases'),
        toolsService.getToolSettings(bookId, 'business-model-canvas')
      ]);
      setCanvases(canvasesData || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Create new canvas
  const handleCreateCanvas = async () => {
    if (!newCanvasName.trim()) return;

    setSaving(true);
    try {
      const newCanvas = {
        name: newCanvasName.trim(),
        description: newCanvasDescription.trim(),
        sections: {},
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const savedCanvas = await toolsService.saveToolData(bookId, 'business-model-canvases', newCanvas);
      setActiveCanvas({ ...newCanvas, id: savedCanvas.id || newCanvas.id });
      setSectionData({});
      setCurrentSectionIndex(0);
      setCurrentAnswers({});
      setGeneratedContent('');
      setShowCreateModal(false);
      setNewCanvasName('');
      setNewCanvasDescription('');
      await loadData();
    } catch (error) {
      console.error('Error creating canvas:', error);
      alert('Failed to create canvas');
    } finally {
      setSaving(false);
    }
  };

  // Select existing canvas
  const handleSelectCanvas = (canvas) => {
    setActiveCanvas(canvas);
    setSectionData(canvas.sections || {});
    
    // Find first incomplete section
    const firstIncompleteIndex = BMC_SECTIONS.findIndex(s => !canvas.sections?.[s.id]?.content);
    setCurrentSectionIndex(firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0);
    
    // Load existing answers if any
    const currentSection = BMC_SECTIONS[firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0];
    setCurrentAnswers(canvas.sections?.[currentSection.id]?.answers || {});
    setGeneratedContent(canvas.sections?.[currentSection.id]?.content || '');
  };

  // Delete canvas
  const handleDeleteCanvas = async (canvasId) => {
    try {
      await toolsService.deleteToolData(bookId, 'business-model-canvases', canvasId);
      await loadData();
    } catch (error) {
      console.error('Error deleting canvas:', error);
    }
  };

  // Generate content with AI
  const handleGenerate = async () => {
    setGenerating(true);
    try {
        const content = await generateSectionContent(
        currentSection,
        currentAnswers,
        sectionData,
        bookId  // Pass bookId instead of apiKey
        );
        setGeneratedContent(content);
    } catch (error) {
        console.error('Generation error:', error);
        alert(error.message);
    } finally {
        setGenerating(false);
    }
    };

  // Save current section
  const handleSaveSection = async () => {
    if (!generatedContent.trim()) {
      alert('Please generate or enter content first');
      return;
    }

    setSaving(true);
    try {
      const updatedSections = {
        ...sectionData,
        [currentSection.id]: {
          answers: currentAnswers,
          content: generatedContent,
          updatedAt: new Date().toISOString()
        }
      };

      const updatedCanvas = {
        ...activeCanvas,
        sections: updatedSections,
        updatedAt: new Date().toISOString()
      };

      await toolsService.saveToolData(bookId, 'business-model-canvases', updatedCanvas);
      setSectionData(updatedSections);
      setActiveCanvas(updatedCanvas);

      // Move to next section if not on last
      if (currentSectionIndex < BMC_SECTIONS.length - 1) {
        const nextIndex = currentSectionIndex + 1;
        setCurrentSectionIndex(nextIndex);
        setCurrentAnswers(updatedSections[BMC_SECTIONS[nextIndex].id]?.answers || {});
        setGeneratedContent(updatedSections[BMC_SECTIONS[nextIndex].id]?.content || '');
      }

      await loadData();
    } catch (error) {
      console.error('Error saving section:', error);
      alert('Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  // Jump to section
  const handleJumpToSection = (index) => {
    setCurrentSectionIndex(index);
    const section = BMC_SECTIONS[index];
    setCurrentAnswers(sectionData[section.id]?.answers || {});
    setGeneratedContent(sectionData[section.id]?.content || '');
  };

  // Handle answer change
  const handleAnswerChange = (questionId, value) => {
    setCurrentAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }

  
  // Create Canvas Modal
  if (showCreateModal) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => setShowCreateModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Create Business Model</h1>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
            <input
              type="text"
              value={newCanvasName}
              onChange={(e) => setNewCanvasName(e.target.value)}
              placeholder="e.g., My Startup Idea"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={newCanvasDescription}
              onChange={(e) => setNewCanvasDescription(e.target.value)}
              placeholder="Brief description of your business idea..."
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl"
            />
          </div>

          <button
            onClick={handleCreateCanvas}
            disabled={!newCanvasName.trim() || saving}
            className="w-full py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Create & Start
          </button>
        </div>
      </div>
    );
  }

  // Canvas List View (no active canvas)
  if (!activeCanvas) {
    return (
      <div className="h-full overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Business Model Canvas</h1>
                <p className="text-gray-500">Map your business strategy using the Strategyzer model</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                New Canvas
              </button>
            </div>
          </div>

          {/* Canvases List */}
          <CanvasesList
            canvases={canvases}
            onSelect={handleSelectCanvas}
            onCreate={() => setShowCreateModal(true)}
            onDelete={handleDeleteCanvas}
          />
        </div>
      </div>
    );
  }

  // Active Canvas - Wizard or Canvas View
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setActiveCanvas(null)} 
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{activeCanvas.name}</h1>
              <p className="text-sm text-gray-500">{activeCanvas.description || 'Business Model Canvas'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* View Mode Toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode(ViewMode.WIZARD)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === ViewMode.WIZARD ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode(ViewMode.CANVAS)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                  viewMode === ViewMode.CANVAS ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === ViewMode.CANVAS ? (
        <CanvasView
          sections={BMC_SECTIONS}
          sectionData={sectionData}
          onEditSection={(section) => {
            const index = BMC_SECTIONS.findIndex(s => s.id === section.id);
            handleJumpToSection(index);
            setViewMode(ViewMode.WIZARD);
          }}
        />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-6 space-y-6">
            {/* Progress */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <ProgressIndicator
                sections={BMC_SECTIONS}
                currentIndex={currentSectionIndex}
                completedSections={sectionData}
                onJumpTo={handleJumpToSection}
              />
            </div>

            {/* Current Section */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Section Header */}
              <div className={`px-6 py-4 bg-gradient-to-r from-${currentSection.color}-50 to-${currentSection.color}-100 border-b border-${currentSection.color}-200`}>
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${currentSection.color}-200`}>
                    <currentSection.icon className={`w-6 h-6 text-${currentSection.color}-700`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{currentSection.name}</h2>
                    <p className="text-sm text-gray-600">{currentSection.description}</p>
                  </div>
                </div>
              </div>

              {/* Questions */}
              <div className="p-6 space-y-6">
                <div className="space-y-5">
                  {currentSection.questions.map(question => (
                    <QuestionInput
                      key={question.id}
                      question={question}
                      value={currentAnswers[question.id]}
                      onChange={handleAnswerChange}
                    />
                  ))}
                </div>

                {/* Generate Button */}
                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !Object.values(currentAnswers).some(v => v?.trim())}
                    className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating with AI...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Generate with AI
                      </>
                    )}
                  </button>
                </div>

                {/* Generated Content Editor */}
                {(generatedContent || sectionData[currentSection.id]?.content) && (
                  <div className="pt-4">
                    <ContentEditor
                      content={generatedContent}
                      onChange={setGeneratedContent}
                      onRegenerate={handleGenerate}
                      generating={generating}
                    />
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                <button
                  onClick={() => handleJumpToSection(Math.max(0, currentSectionIndex - 1))}
                  disabled={currentSectionIndex === 0}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-xl disabled:opacity-50 flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>

                <button
                  onClick={handleSaveSection}
                  disabled={!generatedContent.trim() || saving}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save & {currentSectionIndex < BMC_SECTIONS.length - 1 ? 'Continue' : 'Finish'}
                </button>
              </div>
            </div>

            {/* Section Cards Overview */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">All Sections</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-5 gap-3">
                {BMC_SECTIONS.map((section, index) => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    data={sectionData[section.id]}
                    onClick={() => handleJumpToSection(index)}
                    isEditing={index === currentSectionIndex}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessModelCanvasTool;